import rlModelData from "./rlModelData";
import { McGePoint3d, McDbLine, McCmColor } from "mxcad";

class rlGridShow extends rlModelData {
  constructor() {
    super();
    // 顶点数据
    this.points = [];
    this.Name = null;

    this.classTypeName = "rlGridShow"; // 栅格显示类名
    this._threeObj = null; // 三维图形对象
    this.isSelect = false; // 自定义是否选中标识
    this.m_groupName = "rlGridShowGroup"; // 组名
    this.visible = true; // 初始状态为显示

    this.viewDocCoord = null; // 存储 viewDocCoord
    this.gridSize = 1000; // 存储 gridSize

    // 存储上一次的绘制参数
    this.lastParams = null;

    // 水平线和垂直线实体池，缓存实体引用
    this.horizontalLinesPool = []; // [{ lineId, lineEntity }]
    this.verticalLinesPool = []; // [{ lineId, lineEntity }]
  }

  // 动态绘制
  draw(viewDocCoord, gridSize) {
    if (!this.visible) return;

    // 存储参数
    this.viewDocCoord = viewDocCoord;
    this.gridSize = gridSize;

    const { pt1, pt2, pt3, pt4 } = viewDocCoord;
    const minX = Math.min(pt1.x, pt2.x, pt3.x, pt4.x);
    const maxX = Math.max(pt1.x, pt2.x, pt3.x, pt4.x);
    const minY = Math.min(pt1.y, pt2.y, pt3.y, pt4.y);
    const maxY = Math.max(pt1.y, pt2.y, pt3.y, pt4.y);

    // 计算水平和垂直网格线的数量
    const horizontalLinesCount = Math.ceil((maxY - minY) / gridSize) + 1;
    const verticalLinesCount = Math.ceil((maxX - minX) / gridSize) + 1;

    const maxGridLines = 250;

    let adjustedGridSize = gridSize;

    // 如果网格线数量超过最大值，调整网格间距
    if (
      horizontalLinesCount > maxGridLines ||
      verticalLinesCount > maxGridLines
    ) {
      const horizontalScaleFactor = horizontalLinesCount / maxGridLines;
      const verticalScaleFactor = verticalLinesCount / maxGridLines;
      const scaleFactor = Math.max(horizontalScaleFactor, verticalScaleFactor);

      adjustedGridSize = gridSize * scaleFactor;
    }

    // 检查参数是否发生变化，如果没有变化则不重新绘制
    const currentParams = {
      minX,
      maxX,
      minY,
      maxY,
      gridSize: adjustedGridSize,
    };

    if (this._isParamsEqual(this.lastParams, currentParams)) {
      return; // 参数未变化，跳过绘制
    }

    // 更新上一次的参数
    this.lastParams = currentParams;

    // 使用调整后的网格间距进行绘制
    this.fixedDraw(minX, maxX, minY, maxY, adjustedGridSize);
  }

  // 判断两个参数对象是否相等
  _isParamsEqual(params1, params2) {
    if (!params1 || !params2) return false;
    const threshold = 1; // 设置一个阈值，避免因微小变化导致重绘
    return (
      Math.abs(params1.minX - params2.minX) < threshold &&
      Math.abs(params1.maxX - params2.maxX) < threshold &&
      Math.abs(params1.minY - params2.minY) < threshold &&
      Math.abs(params1.maxY - params2.maxY) < threshold &&
      params1.gridSize === params2.gridSize
    );
  }

  // 固定绘制
  fixedDraw(minX, maxX, minY, maxY, gridSize) {
    if (!this.visible) return;

    // 计算网格线的起始和结束坐标
    const startX = Math.floor(minX / gridSize) * gridSize;
    const endX = Math.ceil(maxX / gridSize) * gridSize;
    const startY = Math.floor(minY / gridSize) * gridSize;
    const endY = Math.ceil(maxY / gridSize) * gridSize;

    // 计算水平和垂直网格线的数量
    const horizontalLinesCount = Math.ceil((endY - startY) / gridSize) + 1;
    const verticalLinesCount = Math.ceil((endX - startX) / gridSize) + 1;

    const lineColor = new McCmColor(180, 180, 180);

    // 更新水平网格线
    for (let i = 0; i < horizontalLinesCount; i++) {
      const y = startY + i * gridSize;
      const startPt = new McGePoint3d(startX, y, 0);
      const endPt = new McGePoint3d(endX, y, 0);

      if (i < this.horizontalLinesPool.length) {
        // 更新已有的线
        const lineData = this.horizontalLinesPool[i];
        const lineEntity = lineData.lineEntity;
        lineEntity.startPoint = startPt;
        lineEntity.endPoint = endPt;
        lineEntity.visible = true;
      } else {
        // 创建新的线并缓存实体引用
        const line = new McDbLine();
        line.startPoint = startPt;
        line.endPoint = endPt;
        line.trueColor = lineColor;
        line.setxDataString("lineType", "rlGridShow");
        const lineId = rlAppObj.mxcadObject.drawEntity(line);
        const lineEntity = lineId.getMcDbEntity();
        lineEntity.drawOrder = 1;
        this.horizontalLinesPool.push({ lineId, lineEntity });
      }
    }

    // 隐藏多余的水平线
    for (
      let i = horizontalLinesCount;
      i < this.horizontalLinesPool.length;
      i++
    ) {
      const lineData = this.horizontalLinesPool[i];
      lineData.lineEntity.visible = false;
    }

    // 更新垂直网格线
    for (let i = 0; i < verticalLinesCount; i++) {
      const x = startX + i * gridSize;
      const startPt = new McGePoint3d(x, startY, 0);
      const endPt = new McGePoint3d(x, endY, 0);

      if (i < this.verticalLinesPool.length) {
        // 更新已有的线
        const lineData = this.verticalLinesPool[i];
        const lineEntity = lineData.lineEntity;
        lineEntity.startPoint = startPt;
        lineEntity.endPoint = endPt;
        lineEntity.visible = true;
      } else {
        // 创建新的线并缓存实体引用
        const line = new McDbLine();
        line.startPoint = startPt;
        line.endPoint = endPt;
        line.trueColor = lineColor;
        line.setxDataString("lineType", "rlGridShow");
        const lineId = rlAppObj.mxcadObject.drawEntity(line);
        const lineEntity = lineId.getMcDbEntity();
        lineEntity.drawOrder = 1;
        this.verticalLinesPool.push({ lineId, lineEntity });
      }
    }

    // 隐藏多余的垂直线
    for (let i = verticalLinesCount; i < this.verticalLinesPool.length; i++) {
      const lineData = this.verticalLinesPool[i];
      lineData.lineEntity.visible = false;
    }

    // 更新显示
    rlAppObj.rlMxDrawObject.updateDisplay();
  }

  // 删除
  erase() {
    this.visible = false;
    this.horizontalLinesPool.forEach((lineData) => {
      lineData.lineEntity.visible = false;
    });
    this.verticalLinesPool.forEach((lineData) => {
      lineData.lineEntity.visible = false;
    });
    rlAppObj.rlMxDrawObject.updateDisplay();
  }

  // 显隐切换
  toggleVisibility(visible) {
    this.visible = visible;
    this.horizontalLinesPool.forEach((lineData) => {
      lineData.lineEntity.visible = visible;
    });
    this.verticalLinesPool.forEach((lineData) => {
      lineData.lineEntity.visible = visible;
    });
    rlAppObj.rlMxDrawObject.updateDisplay();
  }
}

export { rlGridShow };
